import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Store, Edit, Trash2, Plus, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useRetailers } from '@/hooks/useBundles';
import type { Retailer } from '@/contracts/bundles';
import { createRetailer, updateRetailer, deleteRetailer, uploadRetailerLogo } from '@/data/adminRetailers';

const AdminRetailers = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const { data: retailers = [], isLoading, refetch } = useRetailers();
  const [editingRetailer, setEditingRetailer] = useState<Retailer | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, authLoading, navigate]);

  if (authLoading || !isAdmin) {
    return null;
  }

  const handleEditRetailer = (retailer: Retailer) => {
    setEditingRetailer(retailer);
    setIsEditModalOpen(true);
  };

  const handleDeleteRetailer = async (retailerId: string) => {
    try {
      await deleteRetailer(retailerId);

      toast({
        title: 'Retailer deleted',
        description: 'Retailer removed successfully',
      });
      refetch();
    } catch (error) {
      console.error('Error deleting retailer:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete retailer',
        variant: 'destructive',
      });
    }
  };

  const handleCreateRetailer = async (data: any) => {
    try {
      const slug = data.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      
      await createRetailer({
        name: data.name,
        slug,
        description: data.description,
        logo_url: data.logo_url,
        affiliate_base_url: data.affiliate_base_url,
      });

      toast({
        title: 'Retailer created',
        description: 'New retailer added successfully',
      });
      refetch();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating retailer:', error);
      toast({
        title: 'Error',
        description: 'Failed to create retailer',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2">Manage Retailers</h1>
              <p className="text-muted-foreground">
                Add and edit retailers for gear bundles
              </p>
            </div>
            <CreateRetailerModal
              open={isCreateModalOpen}
              onOpenChange={setIsCreateModalOpen}
              onSubmit={handleCreateRetailer}
            />
          </div>

          {isLoading ? (
            <div className="text-center py-12">Loading retailers...</div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="h-5 w-5" />
                  All Retailers ({retailers.length})
                </CardTitle>
                <CardDescription>
                  Manage retailer information and logos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Logo</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Affiliate URL</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {retailers.map((retailer) => (
                      <TableRow key={retailer.id}>
                        <TableCell>
                          {retailer.logo_url ? (
                            <img 
                              src={retailer.logo_url} 
                              alt={retailer.name}
                              className="h-8 w-8 object-contain"
                            />
                          ) : (
                            <div className="h-8 w-8 bg-muted rounded flex items-center justify-center text-xs">
                              No logo
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{retailer.name}</TableCell>
                        <TableCell className="font-mono text-xs">{retailer.slug}</TableCell>
                        <TableCell className="text-xs truncate max-w-xs">
                          {retailer.affiliate_base_url || '—'}
                        </TableCell>
                        <TableCell>
                          {retailer.is_active ? (
                            <span className="text-green-600">Active</span>
                          ) : (
                            <span className="text-muted-foreground">Inactive</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditRetailer(retailer)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Retailer?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete {retailer.name}. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteRetailer(retailer.id)}>
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />

      {editingRetailer && (
        <EditRetailerModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          retailer={editingRetailer}
          onSave={() => {
            refetch();
            setIsEditModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

// Create Retailer Modal
const CreateRetailerModal = ({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo_url: '',
    affiliate_base_url: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ name: '', description: '', logo_url: '', affiliate_base_url: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add Retailer
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Retailer</DialogTitle>
          <DialogDescription>
            Create a new retailer for gear bundles
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="logo_url">Logo URL</Label>
            <Input
              id="logo_url"
              type="url"
              placeholder="https://..."
              value={formData.logo_url}
              onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="affiliate_url">Affiliate Base URL</Label>
            <Input
              id="affiliate_url"
              type="url"
              placeholder="https://..."
              value={formData.affiliate_base_url}
              onChange={(e) => setFormData(prev => ({ ...prev, affiliate_base_url: e.target.value }))}
            />
          </div>

          <DialogFooter>
            <Button type="submit">Create Retailer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Retailer Modal
const EditRetailerModal = ({
  open,
  onOpenChange,
  retailer,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  retailer: Retailer;
  onSave: () => void;
}) => {
  const { toast } = useToast();
  const [name, setName] = useState(retailer.name);
  const [description, setDescription] = useState(retailer.description || '');
  const [logoUrl, setLogoUrl] = useState(retailer.logo_url || '');
  const [affiliateUrl, setAffiliateUrl] = useState(retailer.affiliate_base_url || '');
  const [isActive, setIsActive] = useState(retailer.is_active);

  const handleLogoUpload = async (file: File) => {
    try {
      const url = await uploadRetailerLogo(file);
      setLogoUrl(url);

      toast({
        title: 'Logo uploaded',
        description: 'Logo uploaded successfully',
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload logo',
        variant: 'destructive',
      });
    }
  };

  const handleSave = async () => {
    try {
      await updateRetailer(retailer.id, {
        name,
        description,
        logo_url: logoUrl,
        affiliate_base_url: affiliateUrl,
        is_active: isActive,
      });

      toast({
        title: 'Retailer updated',
        description: 'Retailer information saved successfully',
      });
      onSave();
    } catch (error) {
      console.error('Error updating retailer:', error);
      toast({
        title: 'Error',
        description: 'Failed to update retailer',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Retailer: {retailer.name}</DialogTitle>
          <DialogDescription>
            Update retailer information and logo
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex gap-4 items-start">
              {logoUrl && (
                <div className="shrink-0">
                  <img
                    src={logoUrl}
                    alt={name}
                    className="w-20 h-20 object-contain rounded border bg-white"
                  />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <div className="flex gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleLogoUpload(file);
                    }}
                    className="flex-1"
                  />
                </div>
                <Input
                  placeholder="Or paste logo URL"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-affiliate">Affiliate Base URL</Label>
            <Input
              id="edit-affiliate"
              type="url"
              placeholder="https://..."
              value={affiliateUrl}
              onChange={(e) => setAffiliateUrl(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="edit-active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label htmlFor="edit-active">Active</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminRetailers;
